import { useRef, useState } from "react"

import { Link, Location, type PathMatch, matchPath, useLocation } from "react-router-dom"
import { useClickAway } from "react-use"

import { Button } from "@/3514/components"
import { PARTICIPANT_ONBOARDING_ROOT_PATH } from "@/3514/constants"
import { useIsFakeParticipant } from "@/3514/hooks"
import { type IUserModel } from "@/3514/types"
import { buildCssClassName, isUserCoach, isUserManager, isUserParticipant } from "@/3514/utils"
import { ParticipantUrls } from "@/constants"
import { type IUseTranslation, TUseEscapePress, useEscapePress, useTranslation } from "@/hooks"
import Avatar from "@/shared/avatar/Avatar"
import { HeaderDropdownProfileList } from "@/shared/header-dropdown-profile-list"

import "./styles.scss"

type Props = {
    handleLogout: (e) => void
    showOnMobile?: boolean
    user: IUserModel
    className?: string
}

/**
 * @deprecated uses legacy stuff, rewrite A$AP
 */
const UserHeaderDropdown = ({ user, showOnMobile = false, className = "", handleLogout }: Props) => {
    const isFakeParticipant: boolean = useIsFakeParticipant()

    const { t }: IUseTranslation = useTranslation()
    const location: Location = useLocation()

    const isCoaches: boolean = isUserCoach(user) || isUserManager(user)
    const isParticipant: boolean = isUserParticipant(user)

    const [isActiveMenu, setIsActiveMenu] = useState(false)
    const outsideRefClick = useRef<HTMLDivElement>(null)

    const toggleActiveMenu = () => setIsActiveMenu(prev => !prev)

    const showProfile: boolean =
        !Object.values({
            PARTICIPANT_ONBOARDING_ROOT_PATH,
            profile: ParticipantUrls.PROFILE,
            sso: ParticipantUrls.WAITING_LIST,
            ssoSlug: ParticipantUrls.WAITING_LIST_SSO,
            assignModules: ParticipantUrls.ASSIGN_MODULE
        }).some((path: string): PathMatch<string> => matchPath(path, location.pathname)) && !isCoaches

    useClickAway(outsideRefClick, (): void => setIsActiveMenu(false))

    const { handleEscapePress }: TUseEscapePress = useEscapePress.call({})

    return (
        <div
            onKeyDown={handleEscapePress}
            className={buildCssClassName(
                "d-md-block user-header-dropdown",
                {
                    "absolute [z-index:100] top-[10px] right-[15px]": isParticipant, // WISER-5822,
                    "d-none": !showOnMobile,
                    "fake-mode": isFakeParticipant
                },
                className
            )}
            ref={outsideRefClick}
        >
            <div className="dropdown header-nav-dropdown">
                <Button
                    className="flex items-center justify-center w-full max-w-[35px] h-[35px] border-[1px] border-transparent hover:!border-gray-400 focus:!border-gray-400"
                    isContainer
                    id="userDropdownButton"
                    data-testid="userDropdownButton"
                    data-toggle="dropdown"
                    aria-expanded={isActiveMenu}
                    aria-label={t("participantSide.header.dropdown.opener.ariaLabel")}
                    onClick={toggleActiveMenu}
                >
                    <Avatar
                        width={25}
                        height={25}
                        imgClassName="h-full"
                        url={user?.photo}
                        alt={t("common.avatarFallback.alt")}
                    />
                </Button>
            </div>
            <div className={buildCssClassName("dropdown-header-menu", { show: isActiveMenu })}>
                {showProfile && (
                    <Link className="dropdown-header-item" to={ParticipantUrls.PROFILE}>
                        {t("My Profile")}
                    </Link>
                )}
                <Link className="dropdown-header-item" to="#" onClick={handleLogout}>
                    {t("common.signOut")}
                </Link>
                {isFakeParticipant && <HeaderDropdownProfileList />}
            </div>
        </div>
    )
}

export default UserHeaderDropdown
